import { includes } from 'lodash/fp';
import { P, ProductCarousel, Spinner } from '@ahmdigital/ui';
import { PRODUCT_TYPE } from '@ahmdigital/constants';
import { useSelector } from 'react-redux';
import React, { useEffect, useState } from 'react';

import { BuildYourCoverProductType } from '../../../../../../actions/build-your-cover';
import { LoadingContainer, SmallErrorText, StyledH3, StyledSection } from '../../styles';
import { ProductType } from '../../../../../../types/product';
import { RootState } from '../../../../../../reducers';
import CarouselTiles from '../carousel-tiles';
import getCarouselStartIndex from '../../../../utils/get-carousel-start-index';
import PaymentToggles from '../../../components/payment-toggles';
import selectCustomer from '../../../../../../selectors/select-customer';
import useGetRecommendedProductsByAgeAndScale from '../../../../utils/use-get-recommended-products';

const LoadingScreen = () => (
  <LoadingContainer aria-busy="true" aria-live="polite" role="status" aria-label="Loading products">
    <Spinner />
    <P>Loading products</P>
  </LoadingContainer>
);

type CarouselSectionProps = {
  hasBestSellerWithFewerInclusions: boolean;
  isStuck: boolean;
  products: ProductType[];
  productType: BuildYourCoverProductType;
  sectionRef: React.RefObject<HTMLDivElement>;
};

const { HOSPITAL } = PRODUCT_TYPE;

const CarouselSection = ({
  hasBestSellerWithFewerInclusions,
  isStuck,
  productType,
  products,
  sectionRef,
}: CarouselSectionProps) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const { members, scale } = useSelector(selectCustomer);

  const { selectedServices, selectedProducts, invalidProductTypes } = useSelector(
    (state: RootState) => state.buildYourCover,
  );

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const handleMoveLeft = () => {
    if (activeIndex > 0) {
      setActiveIndex(activeIndex - 1);
    }
  };

  const handleMoveRight = () => {
    if (activeIndex < products.length - 1) {
      setActiveIndex(activeIndex + 1);
    }
  };

  const recommendedProducts = useGetRecommendedProductsByAgeAndScale(members?.[0]?.dob!, scale, selectedServices);

  useEffect(() => {
    setActiveIndex(getCarouselStartIndex(recommendedProducts[productType], products, selectedProducts[productType]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommendedProducts[productType], products?.length]);

  return (
    <StyledSection ref={sectionRef}>
      <StyledH3 $counterIndex={productType === HOSPITAL ? 1 : 2} looksLike="h5">
        Select {productType} cover
      </StyledH3>
      {includes(productType, invalidProductTypes) && (
        <SmallErrorText>Please select {productType} cover to continue.</SmallErrorText>
      )}
      <PaymentToggles productType={productType} />

      {products?.length ? (
        <ProductCarousel
          productCount={products.length}
          isExpanded={isExpanded}
          isStuck={isStuck}
          activeIndex={activeIndex}
          handleMoveLeft={handleMoveLeft}
          handleMoveRight={handleMoveRight}
        >
          <CarouselTiles
            products={products}
            hasBestSellerWithFewerInclusions={hasBestSellerWithFewerInclusions}
            isExpanded={isExpanded}
            isStuck={isStuck}
            handleToggle={handleToggle}
          />
        </ProductCarousel>
      ) : (
        <LoadingScreen />
      )}
    </StyledSection>
  );
};

export default CarouselSection;
